import { type ChangeEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Boxes, Building2, Calendar, Coins, Download, FileText, FileUp, Forklift, Gauge, ImagePlus, MapPin, Megaphone, Pencil, Plus, Receipt, Search, ShieldCheck, Tag, Trash2, TrendingUp, Wrench } from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import {
  type CreateMachineInput,
  type MachineAttachmentRecord,
  type MachineAttributeValue,
  type MachineCustomerOption,
  type MachineDetailRecord,
  type MachineListRecord,
  type MachineOperation,
  type MachinePage,
  type MachinesGateway,
  machineTaxSchemeLabels,
  type MachineStatus,
  type MachineTaxScheme,
  type MachineTypeFieldRecord,
  type MachineTypeRecord,
} from '@forq/machines-data';
import { CustomizableMetrics, EmptyState, RecordDeleteButton, SearchSelect, confirmDiscard, describeDeleteOutcome, useContextMenu, useRecordDeleteFlow, type ContextMenuItem } from '@forq/ui';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import type { MastersGateway, MasterEntry } from '@forq/masters-data';
import { renderMachineDocumentHtml, machineDocumentLabels, type MachineDocumentKind } from './documents';

interface MachinesPageProps {
  readonly tenant: TenantSummary;
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: MachinesGateway;
  readonly mastersGateway: MastersGateway;
  readonly deletionGateway: RecordDeletionGateway;
  /** Firmenprofil (Briefkopf) für Datenblatt/Preisschild. */
  readonly companyGateway: CompanyProfileGateway;
  /** Loads per-machine live financials (Werkstattkosten aus Vorgängen, Mieterträge aus Mietpark), keyed by machine number. */
  readonly loadFinancials?: (tenantId: string) => Promise<Readonly<Record<string, MachineFinancials>>>;
  /** Lädt je Maschine die verknüpften Vorgänge (Miete/Service/Auftrag), keyed by machine number. */
  readonly loadOperations?: (tenantId: string) => Promise<Readonly<Record<string, readonly MachineOperation[]>>>;
  readonly mode: 'demo' | 'supabase';
}

const emptyPage: MachinePage = { items: [], total: 0, page: 1, pageSize: 25 };
const statuses: readonly { value: MachineStatus; label: string }[] = [
  { value: 'customer', label: 'Kundenmaschine' },
  { value: 'stock', label: 'Bestand' },
  { value: 'rental', label: 'Mietpark' },
  { value: 'sold', label: 'Verkauft' },
  { value: 'out-of-service', label: 'Außer Betrieb' },
];

function statusLabel(value: MachineStatus): string { return statuses.find((item) => item.value === value)?.label ?? value; }
/** Eine CSV-Zelle für Excel (Semikolon-getrennt): bei Sonderzeichen in Anführungszeichen. */
function csvCell(value: string | number | null): string {
  const text = String(value ?? '');
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
function messageOf(error: unknown): string { return error instanceof Error && error.message ? error.message : 'Maschinendaten konnten nicht geladen werden.'; }
function text(form: FormData, key: string): string { return String(form.get(key) ?? '').trim(); }

/**
 * Verarbeitet ein hochgeladenes Maschinenbild zu einer data:-URI: auf max. 900 px Breite verkleinert
 * (Canvas, JPEG) -- so passt jedes Foto in die Konfiguration und erscheint ohne externen Abruf im
 * Beleg. Bei sehr großem Ergebnis stärkere Kompression.
 */
async function processMachineImage(file: File): Promise<{ dataUrl?: string; error?: string }> {
  const maxBytes = 1_200_000;
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const scale = Math.min(1, 900 / (image.width || 900));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) { resolve({ error: 'Das Bild konnte nicht verarbeitet werden.' }); return; }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        let dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        if (dataUrl.length > maxBytes) dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        resolve({ dataUrl });
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    image.onerror = () => { resolve({ error: 'Das Bild konnte nicht gelesen werden.' }); URL.revokeObjectURL(objectUrl); };
    image.src = objectUrl;
  });
}
function money(value: number): string { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value); }

/** Kompakte Financials-Zeile für die Listenzeile: EK + Mieterträge + Werkstattkosten (nur was > 0 ist). */
function financeChip(purchasePrice: number, fin: MachineFinancials | undefined): ReactNode {
  const parts: string[] = [];
  if (purchasePrice > 0) parts.push(`EK ${money(purchasePrice)}`);
  if (fin && fin.rentalRevenue > 0) parts.push(`Miete ${money(fin.rentalRevenue)}`);
  if (fin && fin.workshopCosts > 0) parts.push(`Werkstatt ${money(fin.workshopCosts)}`);
  return parts.length ? <small className="machines-list__fin">{parts.join(' · ')}</small> : null;
}

function attributeInput(field: MachineTypeFieldRecord, value: MachineAttributeValue | undefined) {
  const name = `attribute:${field.key}`;
  if (field.type === 'boolean') {
    return <label className="machines-check"><input name={name} type="checkbox" defaultChecked={value === true} /> Ja</label>;
  }
  if (field.type === 'select') {
    return <SearchSelect name={name} defaultValue={typeof value === 'string' ? value : ''} placeholder="Bitte wählen" options={field.options.map((option) => ({ value: option, label: option }))} />;
  }
  if (field.type === 'multiselect') {
    // Checkbox-Gruppe unter EINEM Namen -> FormData.getAll(name) liefert alle Haken als Array.
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="machines-multiselect">
        {field.options.map((option) => (
          <label key={option} className="machines-multiselect__opt">
            <input name={name} type="checkbox" value={option} defaultChecked={selected.includes(option)} /> {option}
          </label>
        ))}
        {field.options.length === 0 ? <span className="machines-muted">Keine Auswahlwerte hinterlegt.</span> : null}
      </div>
    );
  }
  // Kein natives required: In der Workbench liegen inaktive Schritte als hidden im DOM -- ein natives
  // Pflichtfeld dort würde das Absenden mit "not focusable" blockieren. Prüfung erfolgt pro Schritt.
  return <input name={name} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} step={field.type === 'number' ? 'any' : undefined} defaultValue={value === null || value === undefined || Array.isArray(value) ? '' : String(value)} />;
}

interface MachineFieldGroup {
  readonly title: string;
  readonly fields: readonly MachineTypeFieldRecord[];
}

/** Gruppiert die Typ-Felder nach ihrem group-Namen -- eine Gruppe = ein Workbench-Schritt. Reihenfolge
 * der Gruppen nach kleinster sortOrder; Felder ohne Gruppe landen unter "Weitere technische Daten". */
function groupFields(fields: readonly MachineTypeFieldRecord[]): readonly MachineFieldGroup[] {
  const order: string[] = [];
  const grouped = new Map<string, MachineTypeFieldRecord[]>();
  for (const field of [...fields].sort((left, right) => left.sortOrder - right.sortOrder)) {
    // Robust gegen Datenbanken ohne die group-Migration: dann ist field.group undefined -> ein Sammelschritt.
    const title = (field.group ?? '').trim() || 'Weitere technische Daten';
    if (!grouped.has(title)) { grouped.set(title, []); order.push(title); }
    grouped.get(title)!.push(field);
  }
  return order.map((title) => ({ title, fields: grouped.get(title)! }));
}

function readAttributes(form: FormData, type: MachineTypeRecord): Readonly<Record<string, unknown>> {
  const attributes: Record<string, unknown> = {};
  for (const field of type.fields) {
    const name = `attribute:${field.key}`;
    if (field.type === 'boolean') { attributes[field.key] = form.get(name) === 'on'; continue; }
    if (field.type === 'multiselect') {
      const values = form.getAll(name).map((value) => String(value)).filter((value) => value !== '');
      if (values.length > 0) attributes[field.key] = values;
      continue;
    }
    // Leere optionale Felder gar nicht erst mitschicken -- sonst lehnt die Prüfung z. B. einen leeren
    // Select als "invalid option" ab. Nur ausgefüllte Werte landen im Payload.
    const value = text(form, name).trim();
    if (value === '') continue;
    attributes[field.key] = field.type === 'number' ? Number(value) : value;
  }
  return attributes;
}

interface MachineDialogProps {
  readonly machine: MachineDetailRecord | null;
  readonly types: readonly MachineTypeRecord[];
  readonly customers: readonly MachineCustomerOption[];
  readonly warengruppen: readonly MasterEntry[];
  readonly saving: boolean;
  /** Fehler vom Speichern (Gateway/DB) -- wird IM Dialog gezeigt, nicht in der Seite dahinter. */
  readonly saveError: string | null;
  readonly onClose: () => void;
  /** extraImages: weitere Bilder (ohne das Titelbild) -- werden nach dem Speichern als Anhänge angelegt. */
  readonly onSubmit: (input: Omit<CreateMachineInput, 'tenantId'>, extraImages: readonly string[]) => void;
}

function MachineDialog({ machine, types, customers, warengruppen, saving, saveError, onClose, onSubmit }: MachineDialogProps) {
  const [typeId, setTypeId] = useState(machine?.typeId ?? types[0]?.id ?? '');
  // Bilder-Liste: das erste ist das Titelbild (imageUrl am Stamm), die weiteren werden Anhänge.
  const [images, setImages] = useState<string[]>(machine?.imageUrl ? [machine.imageUrl] : []);
  const [imgError, setImgError] = useState('');
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState('');
  const imgInput = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const selectedType = types.find((type) => type.id === typeId) ?? null;
  const groups = useMemo(() => (selectedType ? groupFields(selectedType.fields) : []), [selectedType]);

  // Schritt-Abfolge: Grunddaten -> je Feldgruppe ein Schritt -> Preise -> Bild & Notizen.
  const steps = useMemo(() => [
    { key: 'grunddaten', title: 'Grunddaten' },
    ...groups.map((group) => ({ key: `grp:${group.title}`, title: group.title })),
    { key: 'preise', title: 'Preise' },
    { key: 'medien', title: 'Bild & Notizen' },
  ], [groups]);
  // Beim Typwechsel kann die Schrittzahl schrumpfen -- aktuellen Schritt in Grenzen halten.
  useEffect(() => { setStep((current) => Math.min(current, steps.length - 1)); }, [steps.length]);

  const pickImages = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (files.length === 0) return;
    setImgError('');
    void Promise.all(files.map((file) => processMachineImage(file))).then((results) => {
      const failed = results.find((result) => result.error);
      if (failed?.error) setImgError(failed.error);
      const urls = results.map((result) => result.dataUrl).filter((url): url is string => Boolean(url));
      if (urls.length) setImages((current) => [...current, ...urls]);
    });
  }, []);

  // Keine Pflichtfelder mehr (Nutzerwunsch): Anlegen/Bearbeiten ist jederzeit und aus jedem Schritt
  // speicherbar. Der Maschinentyp bleibt strukturell nötig (er baut das Formular) -- dafür ist der
  // Speichern-Knopf ohne Typ deaktiviert, statt eine Pflichtfeld-Meldung zu werfen.
  const validateStep = useCallback((_key: string): string | null => null, []);

  const goNext = useCallback(() => {
    const problem = validateStep(steps[step].key);
    if (problem) { setStepError(problem); return; }
    setStepError(''); setStep((current) => Math.min(current + 1, steps.length - 1));
  }, [step, steps, validateStep]);
  const goBack = useCallback(() => { setStepError(''); setStep((current) => Math.max(current - 1, 0)); }, []);
  const jumpTo = useCallback((index: number) => { setStepError(''); setStep(index); }, []);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedType) return;
    // Vollständige Prüfung -- beim ersten fehlenden Pflichtfeld zum betroffenen Schritt springen.
    for (let index = 0; index < steps.length; index += 1) {
      const problem = validateStep(steps[index].key);
      if (problem) { setStep(index); setStepError(problem); return; }
    }
    setStepError('');
    const form = new FormData(event.currentTarget);
    const year = text(form, 'constructionYear');
    onSubmit({
      typeId: selectedType.id,
      customerId: text(form, 'customerId') || null,
      manufacturer: text(form, 'manufacturer'),
      model: text(form, 'model'),
      serialNumber: text(form, 'serialNumber'),
      constructionYear: year ? Number(year) : null,
      status: text(form, 'status') as MachineStatus,
      location: text(form, 'location'),
      warengruppeId: text(form, 'warengruppeId') || null,
      imageUrl: images[0] ?? '',
      purchasePrice: Number(text(form, 'purchasePrice')) || 0,
      salesPrice: Number(text(form, 'salesPrice')) || 0,
      rentalDailyRate: Number(text(form, 'rentalDailyRate')) || 0,
      rentalWeeklyRate: Number(text(form, 'rentalWeeklyRate')) || 0,
      rentalMonthlyRate: Number(text(form, 'rentalMonthlyRate')) || 0,
      rentalMinimumDays: Number(text(form, 'rentalMinimumDays')) || 1,
      notes: text(form, 'notes'),
      attributes: readAttributes(form, selectedType),
    }, images.slice(1));
  }

  const activeKey = steps[step]?.key ?? 'grunddaten';
  const isLast = step === steps.length - 1;
  const editingType = machine && selectedType && machine.typeId === selectedType.id;

  return (
    <div className="machines-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="machines-modal__panel machines-wizard" ref={formRef} onSubmit={submit}>
        <div className="machines-wizard__head">
          <span className="machines-eyebrow">{machine ? `Maschine ${machine.number}` : 'Neue Maschine'}</span>
          <h2>{machine ? 'Maschine bearbeiten' : 'Maschine anlegen'}</h2>
        </div>
        <ol className="machines-steps">
          {steps.map((item, index) => (
            <li key={item.key} className={index === step ? 'is-active' : index < step ? 'is-done' : ''}>
              <button type="button" onClick={() => jumpTo(index)}><span className="machines-steps__no">{index + 1}</span>{item.title}</button>
            </li>
          ))}
        </ol>

        <div className="machines-wizard__body">
          {/* Schritt: Grunddaten */}
          <div className="machines-step" hidden={activeKey !== 'grunddaten'}>
            <div className="machines-form-grid">
              <label>Maschinentyp<SearchSelect name="typeId" value={typeId} onChange={setTypeId} options={types.map((type) => ({ value: type.id, label: type.name }))} /></label>
              <label>Zuordnung<SearchSelect name="customerId" defaultValue={machine?.customerId ?? ''} options={[{ value: '', label: 'Keine Kundenzuordnung' }, ...customers.map((customer) => ({ value: customer.id, label: `${customer.number} · ${customer.displayName}` }))]} /></label>
              <label>Hersteller<input name="manufacturer" defaultValue={machine?.manufacturer ?? ''} /></label>
              <label>Modell<input name="model" defaultValue={machine?.model ?? ''} /></label>
              <label>Seriennummer<input name="serialNumber" defaultValue={machine?.serialNumber ?? ''} /></label>
              <label>Baujahr<input name="constructionYear" type="number" min="1900" max="2100" defaultValue={machine?.constructionYear ?? ''} /></label>
              <label>Status<SearchSelect name="status" defaultValue={machine?.status ?? 'customer'} options={statuses.map((status) => ({ value: status.value, label: status.label }))} /></label>
              <label>Standort<input name="location" defaultValue={machine?.location ?? ''} /></label>
              <label>Warengruppe<SearchSelect name="warengruppeId" defaultValue={machine?.warengruppeId ?? ''} options={[{ value: '', label: 'Keine' }, ...warengruppen.map((entry) => ({ value: entry.id, label: entry.name }))]} /></label>
            </div>
          </div>

          {/* Schritte: je Feldgruppe (technische Daten aus dem Maschinentyp) */}
          {groups.map((group) => (
            <div className="machines-step" key={group.title} hidden={activeKey !== `grp:${group.title}`}>
              {group.title === 'Hubgerüst' ? <p className="machines-step__hint">Nur ausfüllen, falls die Maschine ein Hubgerüst besitzt.</p> : null}
              <div className="machines-form-grid">
                {group.fields.map((field) => {
                  const value = editingType ? machine!.attributes[field.key] : undefined;
                  const caption = `${field.label}${field.unit ? ` (${field.unit})` : ''}`;
                  // Mehrfachauswahl bringt eigene <label>-Checkboxen mit -> nicht in ein Feld-<label> schachteln.
                  return field.type === 'multiselect'
                    ? <div key={field.key} className="machines-field-block"><span>{caption}</span>{attributeInput(field, value)}</div>
                    : <label key={field.key}>{caption}{attributeInput(field, value)}</label>;
                })}
              </div>
            </div>
          ))}

          {/* Schritt: Preise */}
          <div className="machines-step" hidden={activeKey !== 'preise'}>
            <div className="machines-form-grid">
              <label>Einkaufspreis EK €<input name="purchasePrice" type="number" min="0" step="0.01" defaultValue={machine?.purchasePrice ?? 0} /></label>
              <label>Verkaufspreis €<input name="salesPrice" type="number" min="0" step="0.01" defaultValue={machine?.salesPrice ?? 0} /></label>
              <label>Miete/Tag €<input name="rentalDailyRate" type="number" min="0" step="0.01" defaultValue={machine?.rentalDailyRate ?? 0} /></label>
              <label>Miete/Woche €<input name="rentalWeeklyRate" type="number" min="0" step="0.01" defaultValue={machine?.rentalWeeklyRate ?? 0} /></label>
              <label>Miete/Monat €<input name="rentalMonthlyRate" type="number" min="0" step="0.01" defaultValue={machine?.rentalMonthlyRate ?? 0} /></label>
              <label>Mindest-Miettage<input name="rentalMinimumDays" type="number" min="1" step="1" defaultValue={machine?.rentalMinimumDays ?? 1} /></label>
            </div>
          </div>

          {/* Schritt: Bild & Notizen */}
          <div className="machines-step" hidden={activeKey !== 'medien'}>
            <div className="machines-image-field">
              <span>Bilder (das erste ist das Titelbild)</span>
              <div className="machines-wizard-gallery">
                {images.map((url, index) => (
                  <figure key={`${index}:${url.slice(0, 32)}`} className={`machines-gallery__item${index === 0 ? ' is-primary' : ''}`}>
                    <img src={url} alt={index === 0 ? 'Titelbild' : `Bild ${index + 1}`} />
                    {index === 0 ? <figcaption>Titelbild</figcaption> : null}
                    <button type="button" className="machines-gallery__remove" title="Entfernen" onClick={() => setImages((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={14} /></button>
                  </figure>
                ))}
                {images.length === 0 ? <span className="machines-image--empty">Keine Bilder</span> : null}
              </div>
              <div className="machines-image__actions">
                <input ref={imgInput} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={pickImages} />
                <button type="button" className="machines-secondary" onClick={() => imgInput.current?.click()}>Bilder hochladen</button>
              </div>
              {imgError && <span className="machines-image__error">{imgError}</span>}
            </div>
            <label className="machines-notes-field">Interne Notizen / Bemerkungen<textarea name="notes" rows={4} defaultValue={machine?.notes ?? ''} /></label>
          </div>
        </div>

        {(stepError || saveError) ? <p className="machines-step__error" role="alert">{stepError || saveError}</p> : null}
        <div className="machines-wizard__nav">
          <button type="button" className="machines-secondary" onClick={onClose}>Abbrechen</button>
          <div className="machines-wizard__navright">
            <span className="machines-wizard__count">Schritt {step + 1} von {steps.length}</span>
            {step > 0 ? <button type="button" className="machines-secondary" onClick={goBack}>Zurück</button> : null}
            {!isLast ? <button type="button" className="machines-secondary" onClick={goNext} disabled={!selectedType}>Weiter</button> : null}
            {/* Jederzeit speicherbar -- nicht erst im letzten Schritt (Nutzerwunsch). */}
            <button type="submit" className="machines-primary" disabled={saving || !selectedType}>{saving ? 'Speichert …' : 'Speichern'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

export interface MachineFinancials {
  readonly workshopCosts: number;
  readonly rentalRevenue: number;
}

const detailTabs: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'technik', label: 'Technik' },
  { id: 'preise', label: 'Preise' },
  { id: 'dokumente', label: 'Bilder & Dokumente' },
  { id: 'vorgaenge', label: 'Vorgänge' },
  { id: 'historie', label: 'Historie' },
];

const categoryLabel: Record<MachineStatus, string> = {
  customer: 'Kundenmaschine', stock: 'Bestand', rental: 'Mietmaschine', sold: 'Verkauft', 'out-of-service': 'Außer Betrieb',
};

const operationKindLabel: Record<MachineOperation['kind'], string> = { rental: 'Miete', service: 'Service', order: 'Auftrag' };

function attributeText(field: MachineTypeFieldRecord, value: MachineAttributeValue | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return `${String(value)}${field.unit ? ` ${field.unit}` : ''}`;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}
function formatBytes(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
const maxDocumentBytes = 8 * 1024 * 1024;

function MachineDetail({ detail, selectedType, warengruppen, canEdit, onEdit, financials, onDelete, gateway, tenantId, company, operations, onDetailChange }: {
  readonly detail: MachineDetailRecord;
  readonly selectedType: MachineTypeRecord | null;
  readonly warengruppen: readonly MasterEntry[];
  readonly canEdit: boolean;
  readonly onEdit: () => void;
  readonly financials: MachineFinancials | null;
  /** Der fertige Löschknopf -- die Seite baut ihn, das Detail zeigt ihn nur an. */
  readonly onDelete: ReactNode;
  readonly gateway: MachinesGateway;
  readonly tenantId: string;
  readonly company: TenantCompanyProfile;
  readonly operations: readonly MachineOperation[];
  readonly onDetailChange: (updated: MachineDetailRecord) => void;
}) {
  const [tab, setTab] = useState('overview');
  const [notice, setNotice] = useState<string | null>(null);
  const [docKind, setDocKind] = useState<MachineDocumentKind | null>(null);
  const [attachments, setAttachments] = useState<readonly MachineAttachmentRecord[]>([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState(detail.notes);
  const [notesSaving, setNotesSaving] = useState(false);
  const [uvvEditing, setUvvEditing] = useState(false);
  const [taxSchemeSaving, setTaxSchemeSaving] = useState(false);
  const [uvvNextDraft, setUvvNextDraft] = useState(detail.nextUvvDate ?? '');
  const [uvvLastDraft, setUvvLastDraft] = useState(detail.lastUvvDate ?? '');
  const [uvvSaving, setUvvSaving] = useState(false);
  const imgInput = useRef<HTMLInputElement>(null);
  const docInput = useRef<HTMLInputElement>(null);
  const fields = selectedType?.fields ?? [];
  const fieldGroups = useMemo(() => groupFields(fields), [fields]);
  const margin = detail.salesPrice > 0 ? detail.salesPrice - detail.purchasePrice : null;
  const images = attachments.filter((item) => item.kind === 'image');
  const documents = attachments.filter((item) => item.kind === 'document');

  const reloadAttachments = useCallback(() => {
    let active = true;
    gateway.listAttachments(tenantId, detail.id)
      .then((list) => { if (active) setAttachments(list); })
      .catch(() => { if (active) setAttachments([]); });
    return () => { active = false; };
  }, [gateway, tenantId, detail.id]);
  useEffect(() => reloadAttachments(), [reloadAttachments]);
  useEffect(() => { setNotesDraft(detail.notes); setNotesEditing(false); }, [detail.id, detail.notes]);
  useEffect(() => {
    setUvvNextDraft(detail.nextUvvDate ?? ''); setUvvLastDraft(detail.lastUvvDate ?? ''); setUvvEditing(false);
  }, [detail.id, detail.nextUvvDate, detail.lastUvvDate]);

  const addImages = useCallback(async (files: readonly File[]) => {
    if (files.length === 0) return;
    setAttachError(''); setAttachBusy(true);
    try {
      for (const file of files) {
        const result = await processMachineImage(file);
        if (result.error || !result.dataUrl) throw new Error(result.error || 'Ein Bild konnte nicht verarbeitet werden.');
        await gateway.addAttachment({ tenantId, machineId: detail.id, kind: 'image', title: file.name || 'Bild', mimeType: 'image/jpeg', byteSize: result.dataUrl.length, content: result.dataUrl });
      }
      reloadAttachments();
    } catch (uploadError) { setAttachError(messageOf(uploadError)); }
    finally { setAttachBusy(false); }
  }, [gateway, tenantId, detail.id, reloadAttachments]);

  const addDocument = useCallback(async (file: File) => {
    setAttachError(''); setAttachBusy(true);
    try {
      if (file.size > maxDocumentBytes) throw new Error('Das Dokument ist größer als 8 MB.');
      const dataUrl = await readFileAsDataUrl(file);
      await gateway.addAttachment({ tenantId, machineId: detail.id, kind: 'document', title: file.name || 'Dokument', mimeType: file.type || 'application/octet-stream', byteSize: file.size, content: dataUrl });
      reloadAttachments();
    } catch (uploadError) { setAttachError(messageOf(uploadError)); }
    finally { setAttachBusy(false); }
  }, [gateway, tenantId, detail.id, reloadAttachments]);

  const removeAttachment = useCallback(async (id: string) => {
    setAttachError('');
    try { await gateway.removeAttachment(tenantId, id); reloadAttachments(); }
    catch (removeError) { setAttachError(messageOf(removeError)); }
  }, [gateway, tenantId, reloadAttachments]);

  const downloadDocument = useCallback(async (item: MachineAttachmentRecord) => {
    setAttachError('');
    try {
      const full = await gateway.getAttachment(tenantId, item.id);
      const link = document.createElement('a');
      link.href = full.content; link.download = full.title; document.body.appendChild(link); link.click(); link.remove();
    } catch (downloadError) { setAttachError(messageOf(downloadError)); }
  }, [gateway, tenantId]);

  const saveNotes = useCallback(async () => {
    setNotesSaving(true);
    try {
      const updated = await gateway.updateMachineNotes(tenantId, detail.id, notesDraft);
      onDetailChange(updated); setNotesEditing(false);
    } catch (notesError) { setNotice(messageOf(notesError)); }
    finally { setNotesSaving(false); }
  }, [gateway, tenantId, detail.id, notesDraft, onDetailChange]);

  /**
   * Besteuerungsart umschalten (§ 25a). Sie entscheidet sich beim Ankauf: Wer von privat kauft,
   * hat keine Vorsteuer -- dann wird beim Verkauf nur die Marge besteuert und die Rechnung weist
   * keine Umsatzsteuer aus. Deshalb hier in der Akte und nicht in der Anlegemaske: sie gehoert
   * zum Gegenstand, nicht zu Hersteller und Seriennummer.
   */
  const saveTaxScheme = useCallback(async (scheme: MachineTaxScheme) => {
    setTaxSchemeSaving(true);
    try {
      onDetailChange(await gateway.setMachineTaxScheme(tenantId, detail.id, scheme));
    } catch (schemeError) { setNotice(messageOf(schemeError)); }
    finally { setTaxSchemeSaving(false); }
  }, [gateway, tenantId, detail.id, onDetailChange]);

  const saveUvv = useCallback(async () => {
    setUvvSaving(true);
    try {
      const updated = await gateway.setMachineInspection(tenantId, detail.id, uvvNextDraft || null, uvvLastDraft || null);
      onDetailChange(updated); setUvvEditing(false);
    } catch (uvvError) { setNotice(messageOf(uvvError)); }
    finally { setUvvSaving(false); }
  }, [gateway, tenantId, detail.id, uvvNextDraft, uvvLastDraft, onDetailChange]);

  return (
    <>
      <div className="machines-detail__head">
        <div className="machines-detail__photo">
          {detail.imageUrl
            ? <img src={detail.imageUrl} alt={`${detail.manufacturer} ${detail.model}`} />
            : <div className="machines-detail__photo--empty"><Boxes size={44} /><span>Kein Bild</span></div>}
        </div>
        <div className="machines-detail__headinfo">
          <span className="machines-eyebrow">{categoryLabel[detail.status]}</span>
          <h2>{detail.manufacturer} {detail.model}</h2>
          <p>{detail.number} · SN {detail.serialNumber || '–'} · {detail.typeName}</p>
          <div className="machines-detail__meta"><b className={`is-${detail.status}`}>{statusLabel(detail.status)}</b>{detail.salesPrice > 0 ? <span className="machines-price">{money(detail.salesPrice)}</span> : null}</div>
          <div className="machines-detail__actions">
            <button type="button" className="machines-secondary" onClick={() => setDocKind('datenblatt')}><FileText size={15} /> Datenblatt</button>
            <button type="button" className="machines-secondary" onClick={() => setDocKind('preisschild')}><Tag size={15} /> Preisschild</button>
            <button type="button" className="machines-secondary" onClick={() => setDocKind('inserat')}><Megaphone size={15} /> Inserat</button>
            <button type="button" className="machines-secondary" disabled={!canEdit} onClick={onEdit}><Pencil size={15} /> Bearbeiten</button>
            {onDelete}
          </div>
        </div>
      </div>
      {notice ? <div className="machines-notice">{notice}<button type="button" onClick={() => setNotice(null)}>×</button></div> : null}
      <nav className="machines-tabs" aria-label="Maschinen-Detail">{detailTabs.map((item) => <button type="button" key={item.id} className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}>{item.label}</button>)}</nav>
      <div className="machines-tab">
        {tab === 'overview' ? (
          <>
            <div className="machines-info-grid">
              <article><Building2 size={17} /><span>Kunde</span><strong>{detail.customerName || 'Keine Zuordnung'}</strong><small>{detail.customerNumber}</small></article>
              <article><Gauge size={17} /><span>Seriennummer</span><strong>{detail.serialNumber || '–'}</strong></article>
              <article><Calendar size={17} /><span>Baujahr</span><strong>{detail.constructionYear ?? '–'}</strong></article>
              <article><MapPin size={17} /><span>Standort</span><strong>{detail.location || '–'}</strong></article>
              <article><Tag size={17} /><span>Warengruppe</span><strong>{warengruppen.find((entry) => entry.id === detail.warengruppeId)?.name ?? detail.warengruppeName ?? '–'}</strong></article>
            </div>
            <div className="machines-kpis">
              <CustomizableMetrics
                pageKey="machine-akte"
                tiles={[
                  { key: 'workshop', node: <article><Wrench size={16} /><span>Werkstattkosten</span><strong>{financials ? money(financials.workshopCosts) : '…'}</strong><small>live aus Vorgängen</small></article> },
                  { key: 'rental', node: <article><Forklift size={16} /><span>Mieterträge</span><strong>{financials ? money(financials.rentalRevenue) : '…'}</strong><small>aus dem Mietpark</small></article> },
                  { key: 'margin', node: <article><TrendingUp size={16} /><span>Marge</span><strong>{margin === null ? '–' : money(margin)}</strong><small>Verkauf − Einkauf</small></article> },
                  {
                    key: 'taxScheme',
                    node: (
                      <article>
                        <Receipt size={16} />
                        <span>Besteuerung</span>
                        <strong>{machineTaxSchemeLabels[detail.taxScheme ?? 'standard']}</strong>
                        {canEdit ? (
                          <button
                            type="button"
                            className="machines-detail__linkbtn"
                            disabled={taxSchemeSaving}
                            onClick={() => void saveTaxScheme((detail.taxScheme ?? 'standard') === 'margin' ? 'standard' : 'margin')}
                          >
                            {(detail.taxScheme ?? 'standard') === 'margin' ? 'auf Regelbesteuerung' : 'auf § 25a umstellen'}
                          </button>
                        ) : <small>§ 25a bei Ankauf ohne Vorsteuer</small>}
                      </article>
                    ),
                  },
                ]}
              />
            </div>
            {fields.length > 0 ? (
              <div className="machines-chips">
                {fields.map((field) => { const value = attributeText(field, detail.attributes[field.key]); return value === null ? null : <span key={field.key} className="machines-chip"><em>{field.label}</em> {value}</span>; })}
              </div>
            ) : null}
            <MachineUvv canEdit={canEdit} nextUvvDate={detail.nextUvvDate ?? null} lastUvvDate={detail.lastUvvDate ?? null}
              editing={uvvEditing} nextDraft={uvvNextDraft} lastDraft={uvvLastDraft} saving={uvvSaving}
              onStart={() => { setUvvNextDraft(detail.nextUvvDate ?? ''); setUvvLastDraft(detail.lastUvvDate ?? ''); setUvvEditing(true); }}
              onChangeNext={setUvvNextDraft} onChangeLast={setUvvLastDraft}
              onCancel={() => { setUvvNextDraft(detail.nextUvvDate ?? ''); setUvvLastDraft(detail.lastUvvDate ?? ''); setUvvEditing(false); }}
              onSave={() => void saveUvv()} />
            <MachineNotes canEdit={canEdit} notes={detail.notes} editing={notesEditing} draft={notesDraft} saving={notesSaving}
              onStart={() => { setNotesDraft(detail.notes); setNotesEditing(true); }} onChange={setNotesDraft}
              onCancel={() => { setNotesDraft(detail.notes); setNotesEditing(false); }} onSave={() => void saveNotes()} />
          </>
        ) : null}
        {tab === 'technik' ? (
          fields.length === 0 ? <p className="machines-muted">Keine technischen Felder für diesen Typ.</p> : (
            <div className="machines-akte-technik">
              {fieldGroups.map((group) => (
                <section key={group.title} className="machines-schema">
                  <h3>{group.title}</h3>
                  <div>{group.fields.map((field) => <article key={field.key}><span>{field.label}</span><strong>{attributeText(field, detail.attributes[field.key]) ?? 'Nicht hinterlegt'}</strong></article>)}</div>
                </section>
              ))}
            </div>
          )
        ) : null}
        {tab === 'preise' ? (
          <div className="machines-info-grid">
            <article><Coins size={17} /><span>Einkauf (EK)</span><strong>{money(detail.purchasePrice)}</strong></article>
            <article><Tag size={17} /><span>Verkaufspreis</span><strong>{detail.salesPrice > 0 ? money(detail.salesPrice) : '–'}</strong></article>
            <article><TrendingUp size={17} /><span>Marge</span><strong>{margin === null ? '–' : money(margin)}</strong></article>
            <article><Wrench size={17} /><span>Werkstattkosten</span><strong>{financials ? money(financials.workshopCosts) : '…'}</strong><small>live aus Vorgängen</small></article>
            <article><Forklift size={17} /><span>Mieterträge</span><strong>{financials ? money(financials.rentalRevenue) : '…'}</strong><small>aus dem Mietpark</small></article>
          </div>
        ) : null}
        {tab === 'dokumente' ? (
          <div className="machines-akte">
            {attachError ? <p className="machines-image__error">{attachError}</p> : null}
            <section className="machines-akte__block">
              <div className="machines-akte__blockhead">
                <h3>Bilder</h3>
                <div>
                  <input ref={imgInput} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ''; void addImages(files); }} />
                  <button type="button" className="machines-secondary" disabled={!canEdit || attachBusy} onClick={() => imgInput.current?.click()}><ImagePlus size={15} /> Bilder hinzufügen</button>
                </div>
              </div>
              <div className="machines-gallery">
                {detail.imageUrl ? <figure className="machines-gallery__item is-primary"><img src={detail.imageUrl} alt="Titelbild" /><figcaption>Titelbild</figcaption></figure> : null}
                {images.map((item) => (
                  <figure key={item.id} className="machines-gallery__item">
                    <img src={item.content} alt={item.title} />
                    {canEdit ? <button type="button" className="machines-gallery__remove" title="Entfernen" onClick={() => void removeAttachment(item.id)}><Trash2 size={14} /></button> : null}
                  </figure>
                ))}
                {!detail.imageUrl && images.length === 0 ? <p className="machines-muted">Noch keine Bilder. Das Titelbild wird beim Bearbeiten gesetzt; weitere Fotos hier hinzufügen.</p> : null}
              </div>
            </section>
            <section className="machines-akte__block">
              <div className="machines-akte__blockhead">
                <h3>Dokumente</h3>
                <div>
                  <input ref={docInput} type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt" hidden onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void addDocument(file); }} />
                  <button type="button" className="machines-secondary" disabled={!canEdit || attachBusy} onClick={() => docInput.current?.click()}><FileUp size={15} /> Dokument hochladen</button>
                </div>
              </div>
              {documents.length === 0 ? <p className="machines-muted">Noch keine Dokumente (z.B. UVV-Prüfbericht, Datenblatt, Rechnung). Bis 8 MB je Datei.</p> : (
                <ul className="machines-doclist">
                  {documents.map((item) => (
                    <li key={item.id}>
                      <FileText size={16} />
                      <div><strong>{item.title}</strong><small>{[formatBytes(item.byteSize), new Date(item.createdAt).toLocaleDateString('de-DE')].filter(Boolean).join(' · ')}</small></div>
                      <button type="button" className="machines-secondary" onClick={() => void downloadDocument(item)}><Download size={15} /> Öffnen</button>
                      {canEdit ? <button type="button" className="machines-secondary is-danger" title="Entfernen" onClick={() => void removeAttachment(item.id)}><Trash2 size={15} /></button> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
        {tab === 'vorgaenge' ? (
          operations.length === 0
            ? <p className="machines-muted">Für diese Maschine sind noch keine Vorgänge (Miete, Service, Auftrag) erfasst.</p>
            : <ul className="machines-ops">
                {operations.map((op, index) => (
                  <li key={`${op.kind}:${op.number}:${index}`}>
                    <span className={`machines-ops__kind is-${op.kind}`}>{operationKindLabel[op.kind]}</span>
                    <div><strong>{op.number || '—'}</strong><small>{op.title}</small></div>
                    <span className="machines-ops__status">{op.status.replace(/[_-]/g, ' ')}</span>
                    <time>{op.date ? new Date(op.date).toLocaleDateString('de-DE') : '–'}</time>
                  </li>
                ))}
              </ul>
        ) : null}
        {tab === 'historie' ? (
          <dl className="machines-history">
            <div><dt>Im Bestand seit</dt><dd>{detail.activeSince ? new Date(detail.activeSince).toLocaleDateString('de-DE') : '–'}</dd></div>
            {detail.activeUntil ? <div><dt>Abgang</dt><dd>{new Date(detail.activeUntil).toLocaleDateString('de-DE')}</dd></div> : null}
            <div><dt>Angelegt</dt><dd>{new Date(detail.createdAt).toLocaleString('de-DE')}</dd></div>
            <div><dt>Zuletzt geändert</dt><dd>{new Date(detail.updatedAt).toLocaleString('de-DE')}</dd></div>
          </dl>
        ) : null}
      </div>
      {docKind ? <MachineDocumentModal kind={docKind} machine={detail} fields={selectedType?.fields ?? []} company={company} onClose={() => setDocKind(null)} /> : null}
    </>
  );
}

function MachineDocumentModal({ kind, machine, fields, company, onClose }: {
  readonly kind: MachineDocumentKind;
  readonly machine: MachineListRecord;
  readonly fields: readonly MachineTypeFieldRecord[];
  readonly company: TenantCompanyProfile;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => {
    const groups = groupFields(fields).map((group) => ({
      title: group.title,
      specs: group.fields
        .map((field) => ({ label: `${field.label}${field.unit ? ` (${field.unit})` : ''}`, value: attributeText(field, machine.attributes[field.key]) ?? '' }))
        .filter((spec) => spec.value !== ''),
    })).filter((group) => group.specs.length > 0);
    return renderMachineDocumentHtml(kind, {
      company,
      todayIso: new Date().toISOString().slice(0, 10),
      groups,
      machine: {
        number: machine.number, manufacturer: machine.manufacturer, model: machine.model,
        serialNumber: machine.serialNumber, constructionYear: machine.constructionYear,
        typeName: machine.typeName, location: machine.location, statusLabel: statusLabel(machine.status),
        imageUrl: machine.imageUrl, salesPrice: machine.salesPrice,
        rentalDailyRate: machine.rentalDailyRate, rentalWeeklyRate: machine.rentalWeeklyRate, rentalMonthlyRate: machine.rentalMonthlyRate,
      },
    });
  }, [kind, machine, fields, company]);
  return (
    <div className="machines-doc-modal" role="dialog" aria-modal="true" aria-label={machineDocumentLabels[kind]} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="machines-doc-modal__panel">
        <header><span>{machineDocumentLabels[kind]} · {machine.manufacturer} {machine.model}</span><button type="button" className="machines-secondary" onClick={onClose}>Schließen</button></header>
        <iframe className="machines-doc-frame" title={machineDocumentLabels[kind]} srcDoc={html} />
      </div>
    </div>
  );
}

function MachineNotes({ canEdit, notes, editing, draft, saving, onStart, onChange, onCancel, onSave }: {
  readonly canEdit: boolean;
  readonly notes: string;
  readonly editing: boolean;
  readonly draft: string;
  readonly saving: boolean;
  readonly onStart: () => void;
  readonly onChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  return (
    <section className="machines-notes machines-notes--editable">
      <div className="machines-notes__head">
        <span>Sonstiges / Bemerkungen</span>
        {!editing && canEdit ? <button type="button" className="machines-secondary" onClick={onStart}><Pencil size={14} /> Bearbeiten</button> : null}
      </div>
      {editing ? (
        <div className="machines-notes__edit">
          <textarea rows={4} value={draft} onChange={(event) => onChange(event.target.value)} placeholder="Bemerkungen zur Maschine …" />
          <div className="machines-notes__actions">
            <button type="button" className="machines-secondary" onClick={onCancel} disabled={saving}>Abbrechen</button>
            <button type="button" className="machines-primary" onClick={onSave} disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button>
          </div>
        </div>
      ) : (
        <p>{notes || 'Keine Bemerkungen hinterlegt.'}</p>
      )}
    </section>
  );
}

const uvvDayMs = 86_400_000;
/** ISO-Datum (YYYY-MM-DD) → deutsches Format, ohne Zeitzonen-Verschiebung. */
function formatUvvDate(iso: string | null): string {
  if (!iso) return '–';
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : iso;
}
/** Ampel für die nächste Prüfung: überfällig / bald fällig (≤ 30 Tage) / ok / keine. */
function uvvTone(nextUvvDate: string | null): { readonly tone: 'overdue' | 'soon' | 'ok' | 'none'; readonly label: string } {
  if (!nextUvvDate) return { tone: 'none', label: 'Keine Prüfung hinterlegt' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${nextUvvDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return { tone: 'none', label: 'Keine Prüfung hinterlegt' };
  const days = Math.round((due.getTime() - today.getTime()) / uvvDayMs);
  if (days < 0) return { tone: 'overdue', label: `Überfällig seit ${Math.abs(days)} Tg.` };
  if (days === 0) return { tone: 'soon', label: 'Heute fällig' };
  if (days <= 30) return { tone: 'soon', label: `Fällig in ${days} Tg.` };
  return { tone: 'ok', label: `Fällig in ${days} Tg.` };
}

function MachineUvv({ canEdit, nextUvvDate, lastUvvDate, editing, nextDraft, lastDraft, saving, onStart, onChangeNext, onChangeLast, onCancel, onSave }: {
  readonly canEdit: boolean;
  readonly nextUvvDate: string | null;
  readonly lastUvvDate: string | null;
  readonly editing: boolean;
  readonly nextDraft: string;
  readonly lastDraft: string;
  readonly saving: boolean;
  readonly onStart: () => void;
  readonly onChangeNext: (value: string) => void;
  readonly onChangeLast: (value: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  const status = uvvTone(nextUvvDate);
  return (
    <section className="machines-notes machines-notes--editable machines-uvv">
      <div className="machines-notes__head">
        <span className="machines-uvv__title"><ShieldCheck size={15} /> UVV-Prüfung</span>
        {!editing && canEdit ? <button type="button" className="machines-secondary" onClick={onStart}><Pencil size={14} /> Bearbeiten</button> : null}
      </div>
      {editing ? (
        <div className="machines-notes__edit">
          <div className="machines-uvv__fields">
            <label>Nächste Prüfung fällig<input type="date" value={nextDraft} onChange={(event) => onChangeNext(event.target.value)} /></label>
            <label>Letzte Prüfung durchgeführt<input type="date" value={lastDraft} onChange={(event) => onChangeLast(event.target.value)} /></label>
          </div>
          <div className="machines-notes__actions">
            <button type="button" className="machines-secondary" onClick={onCancel} disabled={saving}>Abbrechen</button>
            <button type="button" className="machines-primary" onClick={onSave} disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button>
          </div>
        </div>
      ) : (
        <div className="machines-uvv__view">
          <div className="machines-uvv__row">
            <span>Nächste Prüfung</span>
            <strong>{formatUvvDate(nextUvvDate)}</strong>
            <em className={`machines-uvv__pill is-${status.tone}`}>{status.label}</em>
          </div>
          <div className="machines-uvv__row">
            <span>Letzte Prüfung</span>
            <strong>{formatUvvDate(lastUvvDate)}</strong>
          </div>
        </div>
      )}
    </section>
  );
}

type MachineViewMode = 'split' | 'table' | 'cards';

interface MachineCategory { readonly key: string; readonly label: string; readonly match: (machine: MachineListRecord) => boolean; }
/** Kategorie-Tabs wie in der Vorlage: aus Zustand (Neu/Gebraucht) + Status (Kunde/Miete) abgeleitet. */
const machineCategories: readonly MachineCategory[] = [
  { key: 'all', label: 'Alle', match: () => true },
  { key: 'stock', label: 'Bestand', match: (m) => m.status === 'stock' },
  { key: 'neu', label: 'Neumaschinen', match: (m) => m.attributes.zustand === 'neu' },
  { key: 'gebraucht', label: 'Gebrauchtmaschinen', match: (m) => m.attributes.zustand === 'gebraucht' || m.attributes.zustand === 'generalüberholt' },
  { key: 'customer', label: 'Kundenmaschinen', match: (m) => m.status === 'customer' },
  { key: 'rental', label: 'Mietmaschinen', match: (m) => m.status === 'rental' },
  { key: 'sold', label: 'Verkauft', match: (m) => m.status === 'sold' },
  { key: 'out-of-service', label: 'Außer Betrieb', match: (m) => m.status === 'out-of-service' },
];

/** Betriebsstunden für die Std.-Spalte (aus dem hours-Attribut). */
function machineHours(machine: MachineListRecord): string {
  const raw = machine.attributes.hours;
  return raw === null || raw === undefined || raw === '' ? '–' : String(raw);
}
/** Bis zu {limit} Technik-Kennzahlen (aus den Typ-Feldern + Attributen) für die Karten. */
function machineSpecs(machine: MachineListRecord, types: readonly MachineTypeRecord[], limit: number): readonly { readonly label: string; readonly value: string }[] {
  const type = types.find((item) => item.id === machine.typeId);
  if (!type) return [];
  return [...type.fields]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((field) => ({ label: field.label, value: attributeText(field, machine.attributes[field.key]) ?? '' }))
    .filter((spec) => spec.value !== '')
    .slice(0, limit);
}

function MachineTable({ machines, selectedId, onOpen, onContext }: {
  readonly machines: readonly MachineListRecord[];
  readonly selectedId: string | null;
  readonly onOpen: (machine: MachineListRecord) => void;
  readonly onContext?: (event: ReactMouseEvent, machine: MachineListRecord) => void;
}) {
  if (machines.length === 0) return <p className="machines-muted">Keine Maschinen gefunden.</p>;
  return (
    <div className="machines-table-wrap">
      <table className="machines-table">
        <thead><tr><th>Nr.</th><th>Hersteller</th><th>Typ</th><th>Seriennummer</th><th>Besitzer / Kunde</th><th>Bj.</th><th>Std.</th><th>Status</th></tr></thead>
        <tbody>
          {machines.map((machine) => (
            <tr key={machine.id} className={machine.id === selectedId ? 'is-active' : ''} onClick={() => onOpen(machine)} onContextMenu={onContext ? (event) => onContext(event, machine) : undefined}>
              <td className="machines-table__nr">{machine.number}</td>
              <td>{machine.manufacturer || '—'}</td>
              <td>{machine.model || '—'}</td>
              <td>{machine.serialNumber || '—'}</td>
              <td>{machine.customerName || machine.location || '—'}</td>
              <td>{machine.constructionYear ?? '—'}</td>
              <td>{machineHours(machine)}</td>
              <td><b className={`is-${machine.status}`}>{statusLabel(machine.status)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MachineCards({ machines, types, canEdit, onOpen, onEdit, onDoc, onContext }: {
  readonly machines: readonly MachineListRecord[];
  readonly types: readonly MachineTypeRecord[];
  readonly canEdit: boolean;
  readonly onOpen: (machine: MachineListRecord) => void;
  readonly onEdit: (machine: MachineListRecord) => void;
  readonly onDoc: (machine: MachineListRecord, kind: MachineDocumentKind) => void;
  readonly onContext?: (event: ReactMouseEvent, machine: MachineListRecord) => void;
}) {
  if (machines.length === 0) return <p className="machines-muted">Keine Maschinen gefunden.</p>;
  return (
    <div className="machines-cards">
      {machines.map((machine) => {
        const specs = machineSpecs(machine, types, 6);
        return (
          <article key={machine.id} className="machines-card" onContextMenu={onContext ? (event) => onContext(event, machine) : undefined}>
            <div className="machines-card__photo">
              {machine.imageUrl ? <img src={machine.imageUrl} alt={`${machine.manufacturer} ${machine.model}`} /> : <div className="machines-card__photo--empty"><Boxes size={40} /></div>}
            </div>
            <div className="machines-card__head"><span className="machines-card__cat">{categoryLabel[machine.status]}</span><b className={`is-${machine.status}`}>{statusLabel(machine.status)}</b></div>
            <h3>{machine.manufacturer} {machine.model}</h3>
            <p className="machines-card__sub">{machine.number} · SN {machine.serialNumber || '–'}</p>
            {specs.length ? <div className="machines-card__specs">{specs.map((spec) => <div key={spec.label}><span>{spec.label}</span><strong>{spec.value}</strong></div>)}</div> : null}
            <div className="machines-card__foot"><span>{machine.customerName || tenantOwnerFallback}</span><strong>{machine.salesPrice > 0 ? money(machine.salesPrice) : '—'}</strong></div>
            <div className="machines-card__actions">
              <button type="button" className="machines-secondary" onClick={() => onOpen(machine)}>Übersicht</button>
              <button type="button" className="machines-secondary" onClick={() => onDoc(machine, 'datenblatt')}>Datenblatt</button>
              <button type="button" className="machines-secondary" onClick={() => onDoc(machine, 'preisschild')}>Preisschild</button>
              <button type="button" className="machines-secondary" onClick={() => onDoc(machine, 'inserat')}>Inserat</button>
              <button type="button" className="machines-secondary" disabled={!canEdit} onClick={() => onEdit(machine)}>Bearbeiten</button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
const tenantOwnerFallback = 'Eigenbestand';

export function MachinesPage({ tenant, permissions, enabledFeatureIds, gateway, mastersGateway,
  deletionGateway, companyGateway, loadFinancials, loadOperations, mode }: MachinesPageProps) {
  const navigate = useNavigate();
  const { maschinennummer } = useParams();
  const [types, setTypes] = useState<readonly MachineTypeRecord[]>([]);
  const [customers, setCustomers] = useState<readonly MachineCustomerOption[]>([]);
  const [warengruppen, setWarengruppen] = useState<readonly MasterEntry[]>([]);
  const [query, setQuery] = useState('');
  const [typeId, setTypeId] = useState<string | 'all'>('all');
  const [category, setCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<MachineViewMode>('split');
  const [cardDoc, setCardDoc] = useState<{ readonly machine: MachineListRecord; readonly kind: MachineDocumentKind } | null>(null);
  const [bestandFrom, setBestandFrom] = useState('');
  const [bestandTo, setBestandTo] = useState('');
  const [page, setPage] = useState<MachinePage>(emptyPage);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MachineDetailRecord | null>(null);
  const [financialsMap, setFinancialsMap] = useState<Readonly<Record<string, MachineFinancials>>>({});
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [operationsMap, setOperationsMap] = useState<Readonly<Record<string, readonly MachineOperation[]>>>({});
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialog, setDialog] = useState<'create' | 'edit' | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Erfolgsmeldung nach dem Löschen -- getrennt vom Fehlerzustand, es ist ja keiner. */
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);
  const canCreate = permissions.has('machines.create') && enabledFeatureIds.includes('machines.create');
  const canEdit = permissions.has('machines.edit') && enabledFeatureIds.includes('machines.edit');

  // Vom Startbild aus (?neu) direkt „Maschine aufnehmen" öffnen -- sobald die Typen geladen sind.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('neu') === null || !canCreate || types.length === 0) return;
    openDialog('create');
    setSearchParams((prev) => { prev.delete('neu'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types.length]);

  useEffect(() => {
    let active = true;
    Promise.all([gateway.listMachineTypes(tenant.id), gateway.searchCustomerOptions(tenant.id)])
      .then(([nextTypes, nextCustomers]) => { if (active) { setTypes(nextTypes); setCustomers(nextCustomers); } })
      .catch((loadError: unknown) => { if (active) setError(messageOf(loadError)); });
    return () => { active = false; };
  }, [gateway, tenant.id]);

  useEffect(() => {
    let active = true;
    mastersGateway.listWarengruppen(tenant.id).then((list) => { if (active) setWarengruppen(list); }).catch(() => { if (active) setWarengruppen([]); });
    return () => { active = false; };
  }, [mastersGateway, tenant.id]);

  useEffect(() => {
    if (!loadFinancials) { setFinancialsMap({}); return undefined; }
    let active = true;
    loadFinancials(tenant.id).then((map) => { if (active) setFinancialsMap(map); }).catch(() => { if (active) setFinancialsMap({}); });
    return () => { active = false; };
  }, [loadFinancials, tenant.id]);

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [companyGateway, tenant.id]);

  useEffect(() => {
    if (!loadOperations) { setOperationsMap({}); return undefined; }
    let active = true;
    loadOperations(tenant.id).then((map) => { if (active) setOperationsMap(map); }).catch(() => { if (active) setOperationsMap({}); });
    return () => { active = false; };
  }, [loadOperations, tenant.id]);

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await gateway.searchMachines({ tenantId: tenant.id, query, typeId, bestandFrom, bestandTo, pageSize: 500 });
      setPage(result);
      setSelectedId((current) => current && result.items.some((item) => item.id === current) ? current : result.items[0]?.id ?? null);
    } catch (loadError) { setPage(emptyPage); setSelectedId(null); setError(messageOf(loadError)); }
    finally { setLoading(false); }
  }, [gateway, query, tenant.id, typeId, bestandFrom, bestandTo]);

  useEffect(() => { const timer = window.setTimeout(() => void loadList(), 180); return () => window.clearTimeout(timer); }, [loadList]);
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let active = true; setDetailLoading(true);
    void gateway.getMachine(tenant.id, selectedId).then((result) => { if (active) setDetail(result); }).catch((loadError: unknown) => { if (active) setError(messageOf(loadError)); }).finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [gateway, selectedId, tenant.id]);

  // The machine number in the URL (/maschinen/:maschinennummer) opens that record — deep-linkable.
  useEffect(() => {
    if (!maschinennummer) return;
    const found = page.items.find((item) => item.number === maschinennummer);
    if (found) setSelectedId(found.id);
  }, [maschinennummer, page.items]);

  // Typen frisch nachladen -- z.B. wenn im Admin (anderer Tab) Felder deaktiviert/ergänzt wurden.
  // Der Erfassungsdialog soll immer das AKTUELLE Typ-Schema zeigen, nicht die beim Seitenaufruf gecachten.
  const reloadTypes = useCallback(() => {
    gateway.listMachineTypes(tenant.id).then((nextTypes) => setTypes(nextTypes)).catch(() => {});
  }, [gateway, tenant.id]);
  const openDialog = useCallback((mode: 'create' | 'edit') => { reloadTypes(); setDialog(mode); }, [reloadTypes]);

  // CSV-Export ALLER gefilterten Maschinen (nicht nur der aktuellen Seite) für Excel.
  const exportCsv = useCallback(async () => {
    try {
      const all = await gateway.searchMachines({ tenantId: tenant.id, query, typeId, bestandFrom, bestandTo, pageSize: 1000 });
      const headers = ['Nummer', 'Hersteller', 'Modell', 'Seriennummer', 'Baujahr', 'Typ', 'Status', 'Kunde', 'Standort', 'EK', 'VK', 'Miete/Tag'];
      const rows = all.items.map((m) => [
        m.number, m.manufacturer, m.model, m.serialNumber, m.constructionYear ?? '', m.typeName,
        statusLabel(m.status), m.customerName, m.location, m.purchasePrice, m.salesPrice, m.rentalDailyRate,
      ]);
      const csv = [headers, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
      const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `maschinen-${tenant.numberPrefix}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (exportError) { setError(messageOf(exportError)); }
  }, [gateway, tenant.id, tenant.numberPrefix, query, typeId, bestandFrom, bestandTo]);

  const save = useCallback(async (input: Omit<CreateMachineInput, 'tenantId'>, extraImages: readonly string[] = []) => {
    setSaving(true); setError(null);
    try {
      const saved = dialog === 'edit' && detail
        ? await gateway.updateMachine({ ...input, tenantId: tenant.id, machineId: detail.id })
        : await gateway.createMachine({ ...input, tenantId: tenant.id });
      // Weitere Bilder als Anhänge anlegen (das Titelbild steckt schon in imageUrl). Ein fehlendes
      // Anhang-Backend (Migration noch nicht eingespielt) darf das Anlegen der Maschine nicht kippen.
      for (const url of extraImages) {
        try { await gateway.addAttachment({ tenantId: tenant.id, machineId: saved.id, kind: 'image', title: 'Bild', mimeType: 'image/jpeg', byteSize: url.length, content: url }); }
        catch { /* Zusatzbild übersprungen */ }
      }
      setDialog(null); setDetail(saved); setSelectedId(saved.id); setQuery(saved.number); navigate(`/maschinen/${saved.number}`); await loadList();
    } catch (saveError) { setError(messageOf(saveError)); }
    finally { setSaving(false); }
  }, [detail, dialog, gateway, loadList, tenant.id]);

  const selectedType = useMemo(() => types.find((type) => type.id === detail?.typeId) ?? null, [detail?.typeId, types]);
  const financials = detail ? (financialsMap[detail.number] ?? { workshopCosts: 0, rentalRevenue: 0 }) : null;
  const categoryCounts = useMemo(() => Object.fromEntries(machineCategories.map((cat) => [cat.key, page.items.filter(cat.match).length])), [page.items]);
  const visibleMachines = useMemo(() => page.items.filter((machineCategories.find((cat) => cat.key === category) ?? machineCategories[0]).match), [page.items, category]);
  // In Tabellen-/Kartenansicht: eine explizit per URL geöffnete Maschine wird vollbreit als Detail gezeigt.
  const showFullDetail = viewMode !== 'split' && !!maschinennummer && !!detail;
  const editFromCard = useCallback((machine: MachineListRecord) => {
    void gateway.getMachine(tenant.id, machine.id).then((full) => { setDetail(full); setSelectedId(full.id); openDialog('edit'); }).catch((loadError: unknown) => setError(messageOf(loadError)));
  }, [gateway, tenant.id, openDialog]);

  // FORQ-eigenes Rechtsklick-Menü auf Maschinen-Zeilen (statt des Browser-Menüs).
  const canDelete = permissions.has('machines.delete');
  const { open: openMachineMenu, menu: machineContextMenu } = useContextMenu();
  const { requestDelete: requestMachineDelete, deleteDialog: machineDeleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const machineMenuItems = useCallback((machine: MachineListRecord): ContextMenuItem[] => [
    { label: 'Öffnen', icon: <Boxes size={15} />, onSelect: () => navigate(`/maschinen/${machine.number}`) },
    { label: 'Bearbeiten', icon: <Pencil size={15} />, disabled: !canEdit, onSelect: () => editFromCard(machine) },
    { label: 'Datenblatt', icon: <FileText size={15} />, separatorBefore: true, onSelect: () => setCardDoc({ machine, kind: 'datenblatt' }) },
    { label: 'Preisschild', icon: <Tag size={15} />, onSelect: () => setCardDoc({ machine, kind: 'preisschild' }) },
    { label: 'Inserat', icon: <Megaphone size={15} />, onSelect: () => setCardDoc({ machine, kind: 'inserat' }) },
    {
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDelete, separatorBefore: true,
      onSelect: () => requestMachineDelete({
        entity: 'machine', id: machine.id, noun: 'Maschine', label: `${machine.manufacturer} ${machine.model}`,
        onDone: (outcome) => {
          setDeleteNotice(describeDeleteOutcome(outcome, 'Maschine', `${machine.manufacturer} ${machine.model}`));
          setSelectedId((current) => (current === machine.id ? null : current));
          setDetail((current) => (current?.id === machine.id ? null : current));
          void loadList();
        },
      }),
    },
  ], [navigate, canEdit, canDelete, editFromCard, requestMachineDelete, loadList]);

  if (!enabledFeatureIds.includes('machines.list')) return <EmptyState eyebrow="Maschinenmodul" title="Die Maschinenliste ist nicht freigegeben." description="Rolle und Feature-Konfiguration steuern den Zugriff." />;

  const detailNode = detailLoading
    ? <div className="machines-loading">Maschinendaten werden geladen …</div>
    : detail
      ? <MachineDetail detail={detail} selectedType={selectedType} warengruppen={warengruppen} canEdit={canEdit} onEdit={() => openDialog('edit')} financials={financials} gateway={gateway} tenantId={tenant.id} company={company} operations={operationsMap[detail.number] ?? []} onDetailChange={setDetail} onDelete={<RecordDeleteButton className="machines-secondary is-danger" gateway={deletionGateway} tenantId={tenant.id} entity="machine" id={detail.id} noun="Maschine" label={`${detail.manufacturer} ${detail.model}`} canDelete={permissions.has('machines.delete')} onDone={(outcome) => { setDeleteNotice(describeDeleteOutcome(outcome, 'Maschine', `${detail.manufacturer} ${detail.model}`)); setSelectedId(null); setDetail(null); navigate('/maschinen'); void loadList(); }} />} />
      : <EmptyState eyebrow="Maschinenauswahl" title="Wähle eine Maschine aus." description="Wähle links eine Maschine oder öffne sie aus der Liste/den Karten." />;

  return <div className="machines-page">
    <header className="machines-header"><div><span className="machines-eyebrow">FORQ Machines</span><h1>Maschinenstamm</h1><p>Vollständige Geräteakte mit Technikdaten, Kalkulation, Bildern, Historie und Mehrfachfiltern.</p></div><div className="machines-header__actions"><span className={`machines-mode ${mode === 'demo' ? 'is-demo' : ''}`}>{mode === 'demo' ? 'Demo-Daten' : 'Supabase verbunden'}</span><button className="machines-secondary" type="button" disabled={page.total === 0} onClick={() => void exportCsv()} title="Alle gefilterten Maschinen als CSV exportieren"><Download size={16} /> Export</button><button className="machines-primary" type="button" disabled={!canCreate || types.length === 0} onClick={() => openDialog('create')}><Plus size={17} /> Maschine aufnehmen</button></div></header>
    {error && !dialog ? <div className="machines-error" role="alert">{error}<button type="button" onClick={() => void loadList()}>Neu laden</button></div> : null}
    {deleteNotice ? <div className="machines-notice" role="status">{deleteNotice}<button type="button" onClick={() => setDeleteNotice(null)}>×</button></div> : null}
    {showFullDetail ? (
      <div className="machines-fulldetail">
        <button type="button" className="machines-secondary machines-back" onClick={() => navigate('/maschinen')}>← Zurück zur Übersicht</button>
        {detailNode}
      </div>
    ) : (
      <>
        <nav className="machines-cats" aria-label="Kategorie">
          {machineCategories.map((cat) => <button type="button" key={cat.key} className={category === cat.key ? 'is-active' : ''} onClick={() => setCategory(cat.key)}>{cat.label}<b>{categoryCounts[cat.key] ?? 0}</b></button>)}
        </nav>
        <section className="machines-toolbar">
          <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nummer, Hersteller, Modell, Serie oder Kunde …" /></label>
          <div className="machines-viewtoggle" role="group" aria-label="Ansicht">
            <button type="button" className={viewMode === 'split' ? 'is-active' : ''} onClick={() => setViewMode('split')}>Detail</button>
            <button type="button" className={viewMode === 'table' ? 'is-active' : ''} onClick={() => setViewMode('table')}>Liste</button>
            <button type="button" className={viewMode === 'cards' ? 'is-active' : ''} onClick={() => setViewMode('cards')}>Karten</button>
          </div>
          <SearchSelect value={typeId} onChange={setTypeId} ariaLabel="Maschinentyp" options={[{ value: 'all', label: 'Alle Maschinentypen' }, ...types.map((type) => ({ value: type.id, label: type.name }))]} />
          <div className="machines-bestand" title="Bestand im Zeitraum"><span>Bestand</span><input type="date" aria-label="Bestand von" value={bestandFrom} onChange={(event) => setBestandFrom(event.target.value)} /><span>–</span><input type="date" aria-label="Bestand bis" value={bestandTo} onChange={(event) => setBestandTo(event.target.value)} />{(bestandFrom || bestandTo) ? <button type="button" className="machines-bestand__clear" onClick={() => { setBestandFrom(''); setBestandTo(''); }}>×</button> : null}</div>
          <span>{loading ? 'Suche läuft …' : `${visibleMachines.length} Treffer`}</span>
        </section>
        {viewMode === 'split' ? (
          <div className="machines-workspace">
            <aside className="machines-list">{visibleMachines.map((machine) => <button type="button" key={machine.id} className={machine.id === selectedId ? 'is-active' : ''} onClick={() => navigate(`/maschinen/${machine.number}`)} onContextMenu={(event) => openMachineMenu(event, machineMenuItems(machine))}><span><Boxes size={18} /></span><div><strong>{machine.manufacturer} {machine.model}</strong><small>{machine.number} · {machine.typeName}</small><em>{machine.customerName || machine.location || 'Ohne Zuordnung'}</em>{financeChip(machine.purchasePrice, financialsMap[machine.number])}</div><b className={`is-${machine.status}`}>{statusLabel(machine.status)}</b></button>)}{!loading && visibleMachines.length === 0 ? <p>Keine Maschinen gefunden.</p> : null}</aside>
            <main className="machines-detail">{detailNode}</main>
          </div>
        ) : viewMode === 'table' ? (
          <MachineTable machines={visibleMachines} selectedId={selectedId} onOpen={(machine) => navigate(`/maschinen/${machine.number}`)} onContext={(event, machine) => openMachineMenu(event, machineMenuItems(machine))} />
        ) : (
          <MachineCards machines={visibleMachines} types={types} canEdit={canEdit} onOpen={(machine) => navigate(`/maschinen/${machine.number}`)} onEdit={editFromCard} onDoc={(machine, kind) => setCardDoc({ machine, kind })} onContext={(event, machine) => openMachineMenu(event, machineMenuItems(machine))} />
        )}
      </>
    )}
    {dialog ? <MachineDialog machine={dialog === 'edit' ? detail : null} types={types} customers={customers} warengruppen={warengruppen} saving={saving} saveError={error} onClose={() => setDialog(null)} onSubmit={(input, extraImages) => void save(input, extraImages)} /> : null}
    {cardDoc ? <MachineDocumentModal kind={cardDoc.kind} machine={cardDoc.machine} fields={types.find((type) => type.id === cardDoc.machine.typeId)?.fields ?? []} company={company} onClose={() => setCardDoc(null)} /> : null}
    {machineContextMenu}
    {machineDeleteDialog}
  </div>;
}
